import PropTypes from "prop-types";
import { useState, useEffect, useMemo, useRef, useContext } from "react";
import { context } from "@/Layout";
import { useInView } from "framer-motion";
import useStore from "@/hooks/useStore";
import { NavLink } from "react-router-dom";
import { usePadding } from "@/components/utils/Padding";
import useUtils from "@/hooks/useUtils";
import "./styles.scss";

/* #META
 {
  "NAME": "Bento",
  "CLASSNAME": "bento-grid-component",
  "URL": "http://nicasource.local"
 }
#ENDMETA */

const Bento = ({ data }) => {
  const { bento_componet } = data || {};
  if (!bento_componet) return null;

  const { convertToRelative, isExternal } = useUtils();
  const { bento_content, padding_component } = bento_componet;

  const { cards, card_type } = bento_content || {};

  const [posts, setPosts] = useState();
  const { getItems, requestedItems } = useStore();

  const { introShown } = useContext(context) ?? {};
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const paddingStyle = usePadding(padding_component);

  const cardAnimation = useMemo(
    () => ({
      transform: isInView && introShown ? "none" : "translateX(40px)",
      opacity: isInView && introShown ? 1 : 0,
    }),
    [isInView, introShown]
  );

  const cardLabelAnimation = useMemo(
    () => ({
      transform: isInView && introShown ? "none" : "translateY(-40px)",
      opacity: isInView && introShown ? 1 : 0,
      transition: "all 0.5s ease-in-out 0.2s",
    }),
    [isInView, introShown]
  );

  const postsRequest = {
    type: "posts",
    params: {
      _embed: 1,
      order: "desc",
      orderby: "date",
      per_page: 5,
    },
    showLoading: true,
  };

  const getPosts = () => {
    async function fetchData() {
      await getItems(postsRequest);
      setPosts(requestedItems(postsRequest));
    }

    fetchData();
  };

  const getCategories = (post) => {
    const categories = [];
    if (post._embedded && post._embedded["wp:term"]) {
      post._embedded["wp:term"].forEach((termGroup) => {
        termGroup.forEach((term) => {
          if (term.taxonomy === "category") {
            categories.push(term.name);
          }
        });
      });
    }
    return categories;
  };

  useEffect(() => {
    getPosts();
  }, []);

  if (card_type !== "posts" && !cards) {
    return null;
  }

  return (
    <section className="bento-grid-component" ref={ref} style={paddingStyle}>
      <div className="container-revamp">
        {card_type === "posts" ? (
          <div className="bento-lower-content posts-grid">
            {posts?.map((post, index) => {
              const postImage =
                post._embedded &&
                post._embedded["wp:featuredmedia"] &&
                post._embedded["wp:featuredmedia"][0].source_url
                  ? post._embedded["wp:featuredmedia"][0].source_url
                  : "";
              const categories = getCategories(post);

              return (
                <a
                  key={index}
                  className="bento-card"
                  href={post.link}
                  style={{ backgroundImage: `url(${postImage})`, ...cardAnimation }}
                >
                  <div className="overlay"></div>
                  {post.title && (
                    <div
                      className="bento-card-heading"
                      dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                    />
                  )}
                  {categories.length > 0 && (
                    <div className="post-categories" style={{ ...cardLabelAnimation }}>
                      {categories.map((cat, idx) => (
                        <span key={idx} className="post-category">
                          {cat}
                        </span>
                      ))}
                    </div>
                  )}
                </a>
              );
            })}
          </div>
        ) : (
          <div
            className={`bento-lower-content ${
              card_type === "hours" ? "hours-grid" : "services-grid"
            }`}
          >
            {cards.map((card, index) => {
              const {
                heading_card,
                locations,
                time_location,
                background_image,
                service_url,
                img_position,
              } = card;
              const imagePosition =
                {
                  top: "object-top",
                  center: "object-center",
                  bottom: "object-bottom",
                  left: "object-left",
                  right: "object-right",
                }[img_position] || "object-center";
              const bgUrl = background_image?.url || "";

              return (
                <>
                  {card_type === "services" ? (
                    service_url ? (
                      isExternal(service_url) ? (
                        <a
                          key={index}
                          className="bento-card"
                          href={service_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={cardAnimation}
                        >
                          <div className="overlay"></div>
                          {bgUrl && (
                            <img
                              className={`bento-image ${imagePosition}`}
                              src={bgUrl}
                              alt={heading_card}
                            />
                          )}
                          {heading_card && (
                            <div
                              className="bento-card-heading"
                              dangerouslySetInnerHTML={{ __html: heading_card }}
                            />
                          )}
                        </a>
                      ) : (
                        <NavLink
                          key={index}
                          className="bento-card"
                          to={convertToRelative(service_url)}
                          style={cardAnimation}
                        >
                          <div className="overlay"></div>
                          {bgUrl && (
                            <img
                              className={`bento-image ${imagePosition}`}
                              src={bgUrl}
                              alt={heading_card}
                            />
                          )}
                          {heading_card && (
                            <div
                              className="bento-card-heading"
                              dangerouslySetInnerHTML={{ __html: heading_card }}
                            />
                          )}
                        </NavLink>
                      )
                    ) : (
                      <span key={index} className="bento-card" style={cardAnimation}>
                        <div className="overlay"></div>
                        {bgUrl && (
                          <img
                            className={`bento-image ${imagePosition}`}
                            src={bgUrl}
                            alt={heading_card}
                          />
                        )}
                        {heading_card && (
                          <div
                            className="bento-card-heading"
                            dangerouslySetInnerHTML={{ __html: heading_card }}
                          />
                        )}
                      </span>
                    )
                  ) : (
                    <span
                      key={index}
                      className="bento-card"
                      style={{ ...cardAnimation, backgroundImage: `url(${bgUrl})` }}
                    >
                      <div className="overlay"></div>
                      {heading_card && (
                        <div
                          className="bento-card-heading"
                          dangerouslySetInnerHTML={{ __html: heading_card }}
                        />
                      )}
                      {locations && (
                        <div
                          className="bento-card-locations"
                          dangerouslySetInnerHTML={{ __html: locations }}
                        />
                      )}
                      {time_location && (
                        <p className="bento-card-time">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="25"
                            viewBox="0 0 24 25"
                            fill="none"
                          >
                            <g id="vuesax/linear/clock">
                              <g id="clock">
                                <path
                                  id="Vector"
                                  d="M22 12.5C22 18.02 17.52 22.5 12 22.5C6.48 22.5 2 18.02 2 12.5C2 6.98 6.48 2.5 12 2.5C17.52 2.5 22 6.98 22 12.5Z"
                                  stroke="#FDE5AC"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  id="Vector_2"
                                  d="M15.7099 15.6798L12.6099 13.8298C12.0699 13.5098 11.6299 12.7398 11.6299 12.1098V8.00977"
                                  stroke="#FDE5AC"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </g>
                            </g>
                          </svg>
                          <span>{time_location}</span>
                        </p>
                      )}
                    </span>
                  )}
                </>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

Bento.propTypes = {
  data: PropTypes.shape({
    acf_fc_layout: PropTypes.string,
    bento_componet: PropTypes.shape({
      bento_content: PropTypes.shape({
        card_type: PropTypes.string,
        cards: PropTypes.arrayOf(
          PropTypes.shape({
            heading_card: PropTypes.string,
            locations: PropTypes.string,
            time_location: PropTypes.string,
            service_url: PropTypes.string,
            img_position: PropTypes.string,
            background_image: PropTypes.shape({
              url: PropTypes.string,
              alt: PropTypes.string,
              title: PropTypes.string,
            }),
          })
        ),
      }),
    }),
  }),
};

export default Bento;
